import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";

import i18n, { FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES } from "../i18n";
import { userProfileService } from "../services/userProfileService";
import {
    ALLOWED_THEMES,
    buildSettingsPayload,
    DEFAULT_SETTINGS,
    normalizeSettings,
} from "../constants/profileSettings";
import {
    SYSTEM_THEME_PREFERENCE,
    applyThemePreference,
    resolveThemePreference,
    watchSystemThemePreference,
} from "../theme/themeTokens";

const UserPreferencesContext = createContext(null);

const SUPPORTED_LANGUAGE_SET = new Set(SUPPORTED_LANGUAGES);
const SUPPORTED_THEME_SET = new Set(ALLOWED_THEMES);

const normalizeLanguage = (value) => {
    if (typeof value !== "string") {
        return FALLBACK_LANGUAGE;
    }
    const normalized = value.trim().toLowerCase();
    return SUPPORTED_LANGUAGE_SET.has(normalized) ? normalized : FALLBACK_LANGUAGE;
};

const DEFAULT_NORMALIZED_SETTINGS = normalizeSettings(DEFAULT_SETTINGS);

const normalizeTheme = (value) => {
    if (typeof value !== "string") {
        return DEFAULT_NORMALIZED_SETTINGS.appearance.theme;
    }
    const normalized = value.trim().toLowerCase();
    return SUPPORTED_THEME_SET.has(normalized)
        ? normalized
        : DEFAULT_NORMALIZED_SETTINGS.appearance.theme;
};

const updateDocumentLanguage = (language) => {
    if (typeof document === "undefined") {
        return;
    }
    if (document.documentElement.lang !== language) {
        document.documentElement.lang = language;
    }
};

export const UserPreferencesProvider = ({ children }) => {
    const currentUser = useSelector((state) => state.auth.user);
    const userId = currentUser?.id ?? null;
    const mountedRef = useRef(true);
    const activeUserIdRef = useRef(userId);
    const preferencesRequestRef = useRef(0);
    const appliedLanguageRef = useRef(DEFAULT_NORMALIZED_SETTINGS.appearance.language);
    activeUserIdRef.current = userId;

    const [settingsSnapshot, setSettingsSnapshot] = useState(DEFAULT_NORMALIZED_SETTINGS);
    const [activeTheme, setActiveTheme] = useState(() => (
        resolveThemePreference(DEFAULT_NORMALIZED_SETTINGS.appearance.theme)
    ));
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const applyLanguage = useCallback((language) => {
        const normalized = normalizeLanguage(language);
        updateDocumentLanguage(normalized);
        if (appliedLanguageRef.current !== normalized) {
            appliedLanguageRef.current = normalized;
            void i18n.changeLanguage(normalized).catch((err) => {
                console.error("Failed to change language:", err);
            });
        }
        return normalized;
    }, []);

    useEffect(() => {
        applyLanguage(settingsSnapshot.appearance.language);
    }, [applyLanguage, settingsSnapshot.appearance.language]);

    const applyResolvedThemePreference = useCallback((themePreference) => {
        const appliedTheme = applyThemePreference(themePreference);
        if (mountedRef.current) {
            setActiveTheme(appliedTheme);
        }
        return appliedTheme;
    }, []);

    useEffect(() => {
        const themePreference = normalizeTheme(settingsSnapshot.appearance.theme);
        applyResolvedThemePreference(themePreference);
        if (themePreference !== SYSTEM_THEME_PREFERENCE) {
            return undefined;
        }
        return watchSystemThemePreference((systemTheme) => {
            applyResolvedThemePreference(systemTheme);
        });
    }, [applyResolvedThemePreference, settingsSnapshot.appearance.theme]);

    const applySettings = useCallback((nextSettings, { markLoaded = true } = {}) => {
        if (!mountedRef.current) {
            return false;
        }
        const normalized = normalizeSettings(nextSettings);
        setSettingsSnapshot(normalized);
        setHasLoaded(markLoaded);
        return true;
    }, []);

    const loadPreferences = useCallback(async () => {
        const requestId = ++preferencesRequestRef.current;
        const requestUserId = userId;
        const isCurrentRequest = () => (
            mountedRef.current
            && preferencesRequestRef.current === requestId
            && activeUserIdRef.current === requestUserId
        );

        if (!userId) {
            applySettings(DEFAULT_SETTINGS, { markLoaded: false });
            setError(null);
            setIsLoading(false);
            return null;
        }

        setIsLoading(true);
        setHasLoaded(false);
        setError(null);

        try {
            const profile = await userProfileService.getOwnProfile(userId);
            if (!isCurrentRequest()) {
                return null;
            }
            applySettings(profile?.settings);
            return profile?.settings;
        } catch (err) {
            if (isCurrentRequest()) {
                setError(err);
                throw err;
            }
            return null;
        } finally {
            if (isCurrentRequest()) {
                setIsLoading(false);
            }
        }
    }, [applySettings, userId]);

    useEffect(() => {
        if (!userId) {
            applySettings(DEFAULT_SETTINGS, { markLoaded: false });
            setError(null);
            setIsLoading(false);
            return;
        }
        loadPreferences().catch(() => {
            // error handled in state
        });
    }, [applySettings, loadPreferences, userId]);

    const persistLanguagePreference = useCallback(
        async (nextSettings) => {
            if (!userId) {
                return;
            }
            try {
                await userProfileService.updateSettings(userId, buildSettingsPayload(nextSettings));
            } catch (err) {
                console.error("Failed to save language preference:", err);
            }
        },
        [userId],
    );

    const setLanguagePreference = useCallback(
        (value, options = {}) => {
            setSettingsSnapshot((prev) => {
                const nextLanguage = normalizeLanguage(value);
                if (prev.appearance.language === nextLanguage) {
                    return prev;
                }
                const nextSettings = {
                    ...prev,
                    appearance: {
                        ...prev.appearance,
                        language: nextLanguage,
                    },
                };
                if (options.persist !== false) {
                    void persistLanguagePreference(nextSettings);
                }
                return nextSettings;
            });
        },
        [persistLanguagePreference],
    );

    const persistThemePreference = useCallback(
        async (nextSettings) => {
            if (!userId) {
                return;
            }
            try {
                await userProfileService.updateSettings(userId, buildSettingsPayload(nextSettings));
            } catch (err) {
                console.error("Failed to save theme preference:", err);
            }
        },
        [userId],
    );

    const setThemePreference = useCallback(
        (value, options = {}) => {
            setSettingsSnapshot((prev) => {
                const nextTheme = normalizeTheme(value);
                if (prev.appearance.theme === nextTheme) {
                    return prev;
                }
                const nextSettings = {
                    ...prev,
                    appearance: {
                        ...prev.appearance,
                        theme: nextTheme,
                    },
                };
                if (options.persist !== false) {
                    void persistThemePreference(nextSettings);
                }
                return nextSettings;
            });
        },
        [persistThemePreference],
    );

    const contextValue = useMemo(
        () => ({
            language: settingsSnapshot.appearance.language,
            theme: activeTheme,
            activeTheme,
            themePreference: settingsSnapshot.appearance.theme,
            settings: settingsSnapshot,
            isLoading,
            hasLoaded,
            error,
            refresh: loadPreferences,
            setLanguagePreference,
            setThemePreference,
            syncSettingsSnapshot: applySettings,
        }),
        [
            applySettings,
            error,
            hasLoaded,
            activeTheme,
            isLoading,
            loadPreferences,
            setLanguagePreference,
            setThemePreference,
            settingsSnapshot,
        ],
    );

    return (
        <UserPreferencesContext.Provider value={contextValue}>
            {children}
        </UserPreferencesContext.Provider>
    );
};

export const useUserPreferences = () => {
    const context = useContext(UserPreferencesContext);
    if (!context) {
        throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
    }
    return context;
};
